"use client";
import React, { useMemo } from "react";

import { usePathname, useSearchParams } from "next/navigation";

import { getPaginationUrls } from "~/helpers/get-pagination-urls";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { Nullable } from "~/types/general.types";

import { CombinedButton } from "../combined-button/component";

import { ProgressBar } from "./components/progress-bar/component";
import { DEFAULT_LOAD_MORE_BUTTON_TEXT } from "./constants";

export interface LoadMoreButtonProps {
  disabled?: boolean;
  totalCount?: Nullable<number>;
  itemsCount?: Nullable<number>;
  pageSize: number;
  onClick?: () => void;
  progressBarText?: Nullable<string>;
  loadMoreButtonText?: Nullable<string>;
  pageNumber?: React.RefObject<number>;
  itemsMap?: Map<string, number>;
}

export const LoadMoreButton: React.FC<LoadMoreButtonProps> = ({
  disabled,
  totalCount,
  itemsCount,
  itemsMap,
  pageSize,
  onClick,
  loadMoreButtonText,
  progressBarText,
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const valuesArray = useMemo(
    () => itemsMap && Array.from(itemsMap.values()),
    [itemsMap],
  );

  const initialCount = useMemo(
    () =>
      valuesArray?.length ? valuesArray?.[valuesArray.length - 1] * pageSize : 0,
    [pageSize, valuesArray],
  );

  const normalizedItemCount = itemsCount ?? 0;

  const shownCount = useMemo(() => {
    if (totalCount) {
      const isExceedingTotal =
        normalizedItemCount > totalCount || initialCount > totalCount;

      return isExceedingTotal ? totalCount : initialCount;
    }

    return initialCount;
  }, [totalCount, initialCount, normalizedItemCount]);

  const count = useMemo(
    () =>
      typeof totalCount === "number" && typeof pageSize === "number"
        ? Math.min(totalCount - shownCount, pageSize)
        : 0,
    [totalCount, shownCount, pageSize],
  );

  const checkIfloadMoreButtonTextValid = loadMoreButtonText?.includes("{{number}}");

  const loadMoreButtonTextParsed = useMemo(
    () =>
      (checkIfloadMoreButtonTextValid && loadMoreButtonText
        ? loadMoreButtonText
        : DEFAULT_LOAD_MORE_BUTTON_TEXT
      ).replace("{{number}}", count.toString()),
    [checkIfloadMoreButtonTextValid, count, loadMoreButtonText],
  );

  const pagesArray = itemsMap ? Array.from(itemsMap.values()) : [];
  const nextPage = pagesArray[pagesArray.length - 1] + 1;
  const nextLink = getPaginationUrls({
    nextPage,
    pathname,
    searchParams,
  }).next;

  const handleOnClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onClick && onClick();
  };

  return (
    <>
      <ProgressBar
        total={totalCount}
        current={shownCount}
        progressBarText={progressBarText}
      />
      {normalizedItemCount > 0 && count > 0 && (
        <div data-nosnippet>
          <CombinedButton
            id="load-more-button"
            href={nextLink}
            variant="contained"
            size="large"
            onClick={handleOnClick}
            disabled={disabled}
            {...getTestAutomationProps("load-more-button")}
          >
            {loadMoreButtonTextParsed}
          </CombinedButton>
        </div>
      )}
    </>
  );
};
